/**
 * App version hooks — read the force-update config grid and save one app's
 * per-platform settings from the dashboard settings page.
 *
 * Follows the TanStack Query convention established in `useSliders.js`:
 * a `*Keys` factory for cache consistency, `useQuery` for the read, and
 * `useMutation` that invalidates the cache on success.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAppVersions, saveAppVersion } from "services/appVersions";

/** Query key factory — keeps cache keys consistent across app-version hooks. */
export const appVersionKeys = {
    all: ["app-versions"],
};

/**
 * Fetch the full apps × platforms grid. Shape:
 *   { customer: { ios: { minVersion, downloadUrl }, android: {...} }, vendor, driver }
 */
export const useGetAppVersions = () => {
    const query = useQuery({
        queryKey: appVersionKeys.all,
        queryFn: fetchAppVersions,
    });

    return {
        appVersions: query.data ?? null,
        loading: query.isLoading,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

/** Save one app's config and invalidate the cache so the grid refreshes. */
export const useSaveAppVersion = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: saveAppVersion,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: appVersionKeys.all });
        },
    });
};
